import { useCallback } from 'react';
import {
  useSendNotificationMutation,
  useSendBulkNotificationsMutation,
} from '../features/apis/notificationApi';
import ReactToastify from './useReactToastify';
import useCurrentUser from './useCurrentUser';
// Notification types for better type safety
export type NotificationType =
  | 'employee_added'
  | 'employee_deleted'
  | 'employee_updated'
  | 'employee_verified'
  | 'leave_request'
  | 'leave_approved'
  | 'leave_rejected'
  | 'attendance_marked'
  | 'attendance_regularized'
  | 'subscription_created'
  | 'subscription_renewed'
  | 'subscription_expired'
  | 'payment_success'
  | 'payment_failed'
  | 'grievance_submitted'
  | 'grievance_resolved'
  | 'role_changed'
  | 'document_uploaded'
  | 'general'
  | 'profile_update_approved' // ✅ new
  | 'profile_update_rejected'
  | 'profile_edit_requested';

// Helper function to generate notification messages based on type
export const generateNotificationMessage = (type: NotificationType): string => {
  const messages: Record<NotificationType, string> = {
    employee_added: `Employee has been added to the team successfully`,
    employee_deleted: `Employee has been removed from the system successfully`,
    employee_updated: `Employee profile has been updated successfully`,
    employee_verified: `Employee has been verified successfully`,
    leave_request: `Leave request has been submitted`,
    leave_approved: `Your leave request has been approved`,
    leave_rejected: `Your leave request has been rejected`,
    attendance_marked: `Attendance has been marked`,
    attendance_regularized: `Attendance regularization request has been submitted`,
    subscription_created: `Your subscription has been activated`,
    subscription_renewed: `Your subscription has been renewed`,
    subscription_expired: `Your subscription has expired`,
    payment_success: `Payment was successful`,
    payment_failed: `Payment failed`,
    grievance_submitted: `Grievance has been submitted`,
    grievance_resolved: `Your grievance has been resolved`,
    role_changed: `Your role has been updated`,
    document_uploaded: `Document has been uploaded successfully`,
    general: `You have a new notification`,
    profile_update_approved: `Your profile update request has been approved`,
    profile_update_rejected: `Your profile update request has been rejected`,
    profile_edit_requested: `Profile edit request has been submitted`,
  };

  return messages[type] || `You have a new notification`;
};

interface SendNotificationParams {
  userId: string;
  type: NotificationType;
  message?: string;
  data?: Record<string, string | undefined>;
  showToast?: boolean;
  isAdmin: boolean;
}

interface SendBulkNotificationParams {
  userIds: string[];
  type: NotificationType;
  message?: string;
  data?: Record<string, string | undefined>;
  showToast?: boolean;
}

const useSendNotification = () => {
  const [sendNotification, { isLoading: isSending }] =
    useSendNotificationMutation();
  const user = useCurrentUser();
  const [sendBulkNotifications, { isLoading: isSendingBulk }] =
    useSendBulkNotificationsMutation();

  const send = useCallback(
    async (params: SendNotificationParams) => {
      const {
        userId,
        type,
        message,
        data,
        showToast = false,
        isAdmin,
      } = params;
      const notificationMessage = message || generateNotificationMessage(type);

      try {
        const result = await sendNotification({
          userId,
          type,
          companyId: user?.companyId,
          message: notificationMessage,
          meta: data,
          isAdmin,
        }).unwrap();

        if (showToast) {
          ReactToastify({
            success: true,
            message: 'Notification sent successfully',
          });
        }

        return result;
      } catch (error: any) {
        console.error('Failed to send notification:', error);
        if (showToast) {
          ReactToastify({
            success: false,
            message: error?.data?.log || 'Failed to send notification',
          });
        }
        throw error;
      }
    },
    [sendNotification, user?.companyId]
  );

  const sendBulk = useCallback(
    async (params: SendBulkNotificationParams) => {
      const { userIds, type, message, data, showToast = false } = params;

      const notificationMessage = message || generateNotificationMessage(type);

      try {
        const result = await sendBulkNotifications({
          userIds,
          type,
          message: notificationMessage,
          meta: data,
        }).unwrap();

        if (showToast) {
          ReactToastify({
            success: true,
            message: 'Notifications sent successfully',
          });
        }

        return result;
      } catch (error: any) {
        console.error('Failed to send bulk notifications:', error);
        if (showToast) {
          ReactToastify({
            success: false,
            message: error?.data?.log || 'Failed to send notifications',
          });
        }
        throw error;
      }
    },
    [sendBulkNotifications]
  );

  return {
    sendNotification: send,
    sendBulkNotifications: sendBulk,
    isSending,
    isSendingBulk,
  };
};

export default useSendNotification;
